<script setup lang="ts">
import { toDate_wd_dd_MMMM, roundTo, getSemiSuperficie } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'
import convertAzureIcon from '@/utils/azure-icons'

const storeU = useUtilisateurStore()
const theme = useTheme()
const router = useRouter()

const isLoading = ref(false)
const allSemis = ref<Array<MDBSemi & { _culture: string }>>([])

interface DailyForecastDay {
    iconCode: number
    precipitationProbability: number
    totalLiquid?: { value: number; unit: string }
}
interface DailyForecast {
    date: string
    day: DailyForecastDay
}
const dailyForecasts = ref<DailyForecast[]>([])

const weatherMap = computed(() => {
    const m = new Map<string, DailyForecast>()
    for (const f of dailyForecasts.value) m.set(f.date.slice(0, 10), f)
    return m
})

const siteCS = computed(() => storeU.siteSélectionné?.lettresDappel)
const année = new Date().getFullYear()

// ── Harvest date resolution ────────────────────────────────────────────────
function getHarvestDate(semi: MDBSemi): string {
    const plantDate = new Date(semi.dateDu)
    const plantYear = plantDate.getFullYear()

    const manuelle = new Date(semi.dateRécoltePrévueManuelle)
    if (manuelle > plantDate && manuelle.getFullYear() >= plantYear) return semi.dateRécoltePrévueManuelle

    if (semi.degréJour) {
        const dj = new Date(semi.degréJour.dateRécoltePrévue)
        if (dj.getFullYear() === plantYear) return semi.degréJour.dateRécoltePrévue
        const init = new Date(semi.degréJour.dateRécoltePrévueInitiale)
        if (init.getFullYear() === plantYear) return semi.degréJour.dateRécoltePrévueInitiale
    }

    const rp = new Date(semi.dateRécoltePrévue)
    if (semi.dateRécoltePrévue && rp.getFullYear() >= plantYear) return semi.dateRécoltePrévue

    return ''
}

function daysUntil(dateStr: string): number {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = new Date(dateStr)
    d.setHours(0, 0, 0, 0)
    return Math.round((d.getTime() - today.getTime()) / 86_400_000)
}

function urgencyColor(days: number): string {
    if (days < 0) return 'error'
    if (days <= 3) return 'success'
    if (days <= 10) return 'info'
    return 'primary'
}

function daysLabel(days: number): string {
    if (days < 0) return `${Math.abs(days)}j de retard`
    if (days === 0) return "Aujourd'hui"
    if (days === 1) return 'Demain'
    return `Dans ${days}j`
}

function getDJPct(semi: MDBSemi): number | null {
    if (!semi.degréJour) return null
    const { complété, djNécessaireManuel, djNécessaireCalculé, djRéelAccumulé } = semi.degréJour
    if (complété) return 100
    const nécessaire = djNécessaireManuel || djNécessaireCalculé
    if (!nécessaire) return null
    return Math.min(99, Math.round(djRéelAccumulé / nécessaire * 100))
}

function getDJColor(pct: number): string {
    if (pct >= 90) return 'success'
    if (pct >= 66) return 'warning'
    if (pct >= 33) return 'info'
    return 'primary'
}

// ── Computed upcoming semis ────────────────────────────────────────────────
const upcomingSemis = computed(() =>
    allSemis.value
        .filter(s => !s.fermé)
        .map(s => {
            const harvestDate = getHarvestDate(s)
            const days = harvestDate ? daysUntil(harvestDate) : 9999
            const fw = harvestDate ? weatherMap.value.get(harvestDate.slice(0, 10)) : undefined
            const weather = fw
                ? { icon: convertAzureIcon(fw.day.iconCode, true), prob: fw.day.precipitationProbability, mm: roundTo(fw.day.totalLiquid?.value ?? 0, 1) }
                : null
            return { ...s, _harvestDate: harvestDate, _days: days, _weather: weather }
        })
        .filter(s => s._harvestDate && s._days <= 30)
        .sort((a, b) => a._days - b._days)
)

// ── Data loading ───────────────────────────────────────────────────────────
async function loadWeather() {
    const site = storeU.siteSélectionné
    if (!site?.latitudeGoogleMaps) return
    try {
        const r = await apiOmni.get('api/azuremaps/weather/forecast/daily', {
            params: { latitude: site.latitudeGoogleMaps, longitude: site.longitudeGoogleMaps, forecastDays: 15 },
        })
        if (Array.isArray(r.data.forecasts)) dailyForecasts.value = r.data.forecasts
    } catch { /* non-critical */ }
}

async function load() {
    if (!siteCS.value || !storeU.cultures?.length) return
    try {
        isLoading.value = true
        const [, results] = await Promise.all([
            loadWeather(),
            Promise.all(
                storeU.cultures.map(c =>
                    apiProphet
                        .get(`sites/${siteCS.value}/semis`, { params: { culture: c.nom, année } })
                        .then(r => (r.data as MDBSemi[]).map(s => ({ ...s, _culture: c.nom })))
                        .catch(() => [] as Array<MDBSemi & { _culture: string }>)
                )
            ),
        ])
        allSemis.value = results.flat()
    } finally {
        isLoading.value = false
    }
}

watch([siteCS, () => storeU.cultures?.length], load, { immediate: true })

// ── Dialog buckets ────────────────────────────────────────────────────────
const dialogBucketsRef = useTemplateRef('dialogBucketsRef')

async function openBuckets(semi: MDBSemi) {
    await dialogBucketsRef.value?.openBucketsAsync(semi, null)
}

function nbBoîtes(semi: MDBSemi & { _culture: string }): number {
    return roundTo(getSemiSuperficie(semi) * semi.rendementPrévu / unitésParBoîte(semi._culture), 0)
}

function goToChamp(champ: string) {
    router.push({ path: '/carte', query: { champ } })
}
</script>

<template>
    <v-card class="upcoming-semis-widget" rounded="xl" elevation="0" :loading="isLoading">
        <!-- ── Header ──────────────────────────────────────────────────── -->
        <div class="widget-header d-flex align-center justify-space-between px-5 pt-4 pb-2">
            <div class="d-flex align-center" style="gap: 10px;">
                <v-avatar color="success" variant="tonal" size="34" rounded="lg">
                    <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="19" />
                </v-avatar>
                <span class="widget-title">Semis à venir</span>
            </div>
            <v-btn
                variant="text"
                density="compact"
                color="primary"
                size="small"
                to="/ferme/semis"
                append-icon="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"
            >Voir tout</v-btn>
        </div>

        <!-- ── Empty state ─────────────────────────────────────────────── -->
        <div
            v-if="!isLoading && upcomingSemis.length === 0"
            class="text-center text-medium-emphasis py-6 text-body-2"
        >
            Aucun semi prévu dans les 30 prochains jours
        </div>

        <!-- ── Scroll row ──────────────────────────────────────────────── -->
        <div v-else-if="upcomingSemis.length > 0" class="semis-scroll-area px-4 pb-4">
            <div class="semis-row">
                <v-card
                    v-for="semi in upcomingSemis"
                    :key="semi.id"
                    class="semi-card"
                    rounded="lg"
                    :color="urgencyColor(semi._days)"
                    variant="tonal"
                    flat
                    @click="openBuckets(semi)"
                >
                    <div class="semi-card-body pa-3">
                        <!-- Semi # + Bio icon + Champ chip (top-right, clickable → map) -->
                        <div class="d-flex justify-space-between align-center mb-1" style="gap: 4px;">
                            <span class="text-caption font-weight-bold opacity-90">#{{ semi.numéro }}</span>
                            <div class="d-flex align-center" style="gap: 3px;">
                                <v-icon v-if="semi.estBio" icon="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" size="12" color="success" />
                                <v-chip
                                    size="x-small"
                                    variant="tonal"
                                    prepend-icon="M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z"
                                    :color="urgencyColor(semi._days)"
                                    @click.stop="goToChamp(semi.champ)"
                                >{{ semi.champ }}</v-chip>
                            </div>
                        </div>

                        <!-- Harvest date (prominent) -->
                        <div class="harvest-date font-weight-bold mb-1">
                            {{ toDate_wd_dd_MMMM(semi._harvestDate) }}
                        </div>

                        <!-- Days badge -->
                        <div class="mb-1">
                            <v-chip
                                :color="urgencyColor(semi._days)"
                                size="x-small"
                                variant="flat"
                                class="days-chip"
                            >{{ daysLabel(semi._days) }}</v-chip>
                        </div>

                        <!-- Weather at harvest date -->
                        <div class="mb-2" style="min-height: 20px;">
                            <template v-if="semi._weather">
                                <v-chip
                                    v-if="semi._weather.prob >= 50"
                                    size="x-small"
                                    variant="flat"
                                    :color="semi._weather.prob >= 70 ? 'error' : 'warning'"
                                    class="font-weight-bold"
                                    :prepend-icon="semi._weather.icon"
                                >{{ semi._weather.prob }}%<template v-if="semi._weather.mm > 0">&nbsp;&middot;&nbsp;{{ semi._weather.mm }}&nbsp;mm</template></v-chip>
                                <span v-else class="d-flex align-center text-caption text-medium-emphasis" style="gap: 2px;">
                                    <v-icon :icon="semi._weather.icon" size="13" />
                                    <span>{{ semi._weather.prob }}%</span>
                                </span>
                            </template>
                        </div>

                        <v-divider class="mb-2 opacity-30" />

                        <!-- Culture + variété -->
                        <div class="mb-2" style="line-height: 1.4;">
                            <div class="text-caption font-weight-medium text-truncate" style="max-width: 145px;">
                                {{ semi._culture }}
                            </div>
                            <div class="text-caption text-medium-emphasis text-truncate" style="max-width: 145px;">
                                {{ semi.variété }}
                            </div>
                        </div>

                        <!-- Planches + superficie -->
                        <div class="text-caption text-medium-emphasis mb-1">
                            Pl.&nbsp;{{ semi.plancheDe }}&ndash;{{ semi.plancheÀ }}
                            ({{ semi.plancheÀ - semi.plancheDe + 1 }})
                            &middot; {{ roundTo(getSemiSuperficie(semi), 2) }}&nbsp;ac
                        </div>

                        <!-- Nb boîtes prévues -->
                        <div class="text-caption mb-2">
                            <v-icon icon="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L10.11,5.22L16,8.61L17.96,7.5L12,4.15M6.04,7.5L12,10.85L13.96,9.75L8.08,6.35L6.04,7.5M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z" size="11" class="mr-1 opacity-70" />
                            <span class="font-weight-medium">{{ nbBoîtes(semi) }}</span>
                            <span class="text-medium-emphasis ml-1">bts prévues</span>
                        </div>

                        <!-- Degree-day progress -->
                        <template v-if="getDJPct(semi) !== null">
                            <div class="d-flex align-center" style="gap: 6px;">
                                <v-progress-linear
                                    :model-value="Math.min(getDJPct(semi)!, 100)"
                                    :color="getDJColor(getDJPct(semi)!)"
                                    height="5"
                                    rounded
                                    style="flex: 1;"
                                    bg-color="rgba(var(--v-theme-on-surface), 0.1)"
                                />
                                <span class="text-caption font-weight-bold" style="min-width: 30px; text-align: right;">
                                    {{ getDJPct(semi) }}%
                                </span>
                            </div>
                            <div class="text-caption text-medium-emphasis mt-1">
                                {{ roundTo(semi.degréJour!.djRéelAccumulé, 0) }}&nbsp;/&nbsp;{{ roundTo(semi.degréJour!.djNécessaireManuel || semi.degréJour!.djNécessaireCalculé, 0) }}&nbsp;DJ
                            </div>
                        </template>

                        <v-divider class="my-2 opacity-30" />

                        <!-- Plantation date -->
                        <div class="text-caption text-medium-emphasis">
                            Planté&nbsp;: {{ toDate_wd_dd_MMMM(semi.dateDu) }}
                        </div>
                    </div>
                </v-card>
            </div>
        </div>
    </v-card>

    <DialogBuckets ref="dialogBucketsRef" />
</template>

<style scoped>
.upcoming-semis-widget {
    overflow: hidden;
    background: v-bind('theme.global.name.value === "dark" ? "rgba(30, 38, 56, 0.55)" : "rgba(255, 255, 255, 0.8)"') !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.5)"');
}

.widget-header {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.widget-title {
    font-size: 1rem;
    font-weight: 700;
}

.semis-scroll-area {
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(var(--v-border-color), 0.3) transparent;
}

.semis-row {
    display: flex;
    gap: 10px;
    width: max-content;
    padding-top: 4px;
    padding-bottom: 2px;
}

.semi-card {
    width: 175px;
    cursor: pointer;
    flex-shrink: 0;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.semi-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(var(--v-theme-on-surface), 0.12) !important;
}

.harvest-date {
    font-size: 0.9rem;
    letter-spacing: 0.01em;
}

.days-chip {
    font-weight: 600 !important;
}
</style>
